import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DollarSign, Loader2, Save, AlertTriangle } from "lucide-react";

const ROWS = [
  { plan: "growth", interval: "month", label: "Growth · Monthly", field: "price_monthly" },
  { plan: "growth", interval: "year", label: "Growth · Yearly", field: "price_yearly" },
  { plan: "pro", interval: "month", label: "Pro · Monthly", field: "price_monthly" },
  { plan: "pro", interval: "year", label: "Pro · Yearly", field: "price_yearly" },
];

export default function AdminPricingTab() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [vals, setVals] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/pricing").then((r) => {
      setPlans(r.data.plans);
      const v = {};
      for (const p of r.data.plans) { v[`${p.id}-month`] = p.price_monthly; v[`${p.id}-year`] = p.price_yearly; }
      setVals(v);
      setLoading(false);
    }).catch(() => { toast.error("Could not load pricing."); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const save = async (plan, interval) => {
    const key = `${plan}-${interval}`;
    const amount = Number(vals[key]);
    if (!(amount > 0)) { toast.error("Enter an amount greater than 0."); return; }
    setSavingKey(key);
    try {
      const r = await api.post("/admin/pricing/change", { plan, interval, amount, currency: "usd" });
      toast.success(`Updated — new customers now pay $${r.data.amount} / ${interval}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingKey(null); }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-pricing-tab">
      <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <div className="text-sm text-[#e8c489]">
          <p className="font-semibold text-[#F59E0B]">Changing a price updates Stripe live.</p>
          <p className="mt-1">New checkouts will be charged the new amount immediately. <strong>Existing subscribers keep their current price</strong> until they change plans. The Billing page updates automatically to match.</p>
        </div>
      </div>

      <div className="rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] overflow-hidden" data-testid="pricing-table">
        <div className="p-5 border-b border-[#1a1a1a] flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#10B981]" /><h3 className="font-head font-bold text-white">Subscription Prices (USD)</h3></div>
        <div className="divide-y divide-[#141414]">
          {ROWS.map((row) => {
            const key = `${row.plan}-${row.interval}`;
            const current = plans.find((p) => p.id === row.plan)?.[row.field];
            return (
              <div key={key} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`pricing-row-${key}`}>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm">{row.label}</p>
                  <p className="text-xs text-[#666]">Currently ${current} / {row.interval}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666] text-sm">$</span>
                    <Input data-testid={`price-input-${key}`} type="number" value={vals[key] ?? ""} onChange={(e) => setVals({ ...vals, [key]: e.target.value })} className="h-9 w-32 pl-6 bg-[#111] border-[#262626] text-white" />
                  </div>
                  <Button data-testid={`price-save-${key}`} onClick={() => save(row.plan, row.interval)} disabled={savingKey === key} className="h-9 bg-[#10B981] hover:bg-[#0d9668] text-black font-semibold">
                    {savingKey === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
